import { ReactNode, useRef } from 'react';

import { Box, BoxProps } from '@joinrepublic/dls-ui-kit';
import { motion, useInView, Variants } from 'framer-motion';

import { useMotionBoxGroup } from '../motion-box-group/motion-box-group';

type AnimationEffect = 'fadeSlideIn' | 'fadeIn' | 'fadeOut' | 'scaleFadeIn';

interface MotionBoxProps extends BoxProps {
  children: ReactNode;
  viewportMargin?: string;
  distance?: number | string;
  duration?: number;
  delay?: number;
  easing?: string | number[];
  effect?: AnimationEffect;
  once?: boolean;
  animateRemote?: boolean;
}

const MotionBoxEl = motion(Box);

export function MotionBox({
  children,
  viewportMargin = '-100px',
  distance = '50px',
  duration = 0.5,
  delay = 0,
  easing = 'easeOut',
  effect = 'fadeSlideIn',
  once = true,
  animateRemote = false,
  ...props
}: MotionBoxProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: once,
    margin: viewportMargin,
  });

  const groupContext = useMotionBoxGroup();
  const isGroupInView = groupContext?.isGroupInView ?? false;
  const transition = {
    ease: easing,
    duration: duration,
    delay: delay,
  };

  const variants: Record<AnimationEffect, Variants> = {
    fadeSlideIn: {
      hidden: {
        opacity: 0,
        translateY: distance,
      },
      visible: {
        opacity: 1,
        translateY: 0,
        transition: transition,
      },
    },
    fadeIn: {
      hidden: {
        opacity: 0,
      },
      visible: {
        opacity: 1,
        transition: transition,
      },
    },
    fadeOut: {
      hidden: {
        opacity: 1,
      },
      visible: {
        opacity: 0,
        transition: transition,
      },
    },
    scaleFadeIn: {
      hidden: {
        opacity: 0,
        scale: 0.95,
      },
      visible: {
        opacity: 1,
        scale: 1,
        transition: transition,
      },
    },
  };

  const shouldAnimate = animateRemote || isGroupInView || isInView;

  return (
    <MotionBoxEl
      ref={ref}
      variants={variants[effect]}
      initial="hidden"
      animate={shouldAnimate ? 'visible' : 'hidden'}
      {...props}
    >
      {children}
    </MotionBoxEl>
  );
}
